import { useState } from 'react';

interface Props {
  userId: string | null;
  disabled?: boolean;
}

export default function SendEnvelope({ userId, disabled }: Props) {
  const [signerEmail, setSignerEmail] = useState('');
  const [signerName, setSignerName] = useState('');
  const [subject, setSubject] = useState('Test Document - Please Sign');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<{
    success: boolean;
    envelopeId?: string;
    documentHash?: string;
    error?: string;
  } | null>(null);

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userId || !signerEmail || !signerName) return;

    setLoading(true);
    setResult(null);

    try {
      const response = await fetch('/api/envelope/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId,
          signerEmail,
          signerName,
          subject,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setResult({
          success: true,
          envelopeId: data.envelopeId,
          documentHash: data.documentHash,
        });
        setSignerEmail('');
        setSignerName('');
      } else {
        setResult({ success: false, error: data.error });
      }
    } catch (err) {
      setResult({ success: false, error: 'Failed to send envelope' });
    } finally {
      setLoading(false);
    }
  };

  if (disabled) {
    return (
      <div className="text-gray-400">
        <p className="text-sm">Connect DocuSign first to send test documents.</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSend} className="space-y-3">
      <div>
        <input
          type="email"
          placeholder="Signer email"
          value={signerEmail}
          onChange={(e) => setSignerEmail(e.target.value)}
          className="w-full px-3 py-2 border rounded-md text-sm"
          required
        />
      </div>
      <div>
        <input
          type="text"
          placeholder="Signer name"
          value={signerName}
          onChange={(e) => setSignerName(e.target.value)}
          className="w-full px-3 py-2 border rounded-md text-sm"
          required
        />
      </div>
      <div>
        <input
          type="text"
          placeholder="Subject"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          className="w-full px-3 py-2 border rounded-md text-sm"
        />
      </div>
      <button
        type="submit"
        disabled={loading}
        className="w-full bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
      >
        {loading ? 'Sending...' : 'Send Test Envelope'}
      </button>

      {result && (
        <div
          className={`p-3 rounded-md text-sm ${
            result.success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
          }`}
        >
          {result.success ? (
            <div>
              <p className="font-medium">Envelope sent!</p>
              <p className="text-xs mt-1">ID: {result.envelopeId}</p>
              <p className="text-xs">Hash: {result.documentHash?.substring(0, 16)}...</p>
            </div>
          ) : (
            <p>Error: {result.error}</p>
          )}
        </div>
      )}
    </form>
  );
}
